import { useCallback, useEffect, useState } from 'react';
import { fmtLongDay, fmtTime, relativeTo } from '../lib/time';
import { alert, confirm, haptic } from '../lib/telegram';
import { useT } from '../lib/i18n';

/**
 * Time off: holidays, a tournament, a wedding, an injury.
 *
 * It lives in Setup, next to working hours and the court calendar,
 * because it is the same kind of thing — when you are and are not
 * available — and because a fifth tab for something used a few times a
 * year would crowd the four that get used daily.
 *
 * It had its own tab until Setup replaced it, and nothing has been able
 * to reach it since: the screen existed, the route existed, and no
 * button set the tab. Blocking a week off is not a thing a coach should
 * have to ask support for.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
let authToken = '';
export const setTimeOffToken = (t: string) => { authToken = t; };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    // No body, no content type: the server reads the header as a promise
    // of JSON and refuses the request when none arrives.
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${authToken}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `http_${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export function TimeOffTab() {
  const t = useT();
  const [blocks, setBlocks] = useState<any[] | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');

  const load = useCallback(async () => { setBlocks(await api<any[]>('/api/coach/blocks')); }, []);
  useEffect(() => { load().catch(() => setBlocks([])); }, [load]);

  async function add() {
    if (!from || !to) { await alert(t('co.off.pickRange')); return; }
    try {
      const res = await api<{ clashes: any[] }>('/api/coach/blocks', {
        method: 'POST',
        body: JSON.stringify({ starts_at: new Date(from).toISOString(), ends_at: new Date(to).toISOString(), reason: reason || null }),
      });
      haptic.success();
      if (res.clashes.length > 0) {
        await alert(
          t('co.off.blockedWithClashes', {
            n: res.clashes.length, bookings: t.word('co.cd.bookings', res.clashes.length),
          }) + '\n\n' +
          res.clashes.map((c) => t('co.off.clashList', {
            name: c.name, time: fmtTime(c.starts_at),
          })).join('\n') +
          t('co.off.cancelThem'));
      }
      setFrom(''); setTo(''); setReason(''); load();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.off.blockFailed', { message: err.message }));
    }
  }

  async function remove(id: string) {
    const ok = await confirm(t('co.off.removeBlock'));
    if (!ok) return;
    await api(`/api/coach/blocks/${id}`, { method: 'DELETE' });
    haptic.success(); load();
  }

  return (
    <>
      <div className="section-title">{t('co.off.title')}</div>
      <div className="list">
        <div className="card">
          <label className="card__when">{t('co.off.from')}</label>
          <input type="datetime-local" className="paybar__opt timeoff__date" style={{ marginTop: 4, marginBottom: 10 }}
            value={from} onChange={(e) => setFrom(e.target.value)} />
          <label className="card__when">{t('co.off.until')}</label>
          <input type="datetime-local" className="paybar__opt timeoff__date" style={{ marginTop: 4, marginBottom: 10 }}
            value={to} onChange={(e) => setTo(e.target.value)} />
          <input className="paybar__opt" style={{ width: '100%', textAlign: 'left', marginBottom: 10 }}
            placeholder={t('co.off.reason')} value={reason} onChange={(e) => setReason(e.target.value)} />
          <button className="paybar__opt"
            style={{ width: '100%', background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'var(--accent)' }}
            onClick={add}>{t('co.off.block')}</button>
        </div>
      </div>
      <div className="section-title">{t('co.off.upcoming')}</div>
      {blocks === null ? (
        <div className="list"><div className="skeleton" /></div>
      ) : blocks.length === 0 ? (
        <div className="empty">{t('co.off.nothing')}</div>
      ) : (
        <div className="list">
          {blocks.map((b) => (
            <div className="card" key={b.id}>
              <div className="card__head">
                {/* The reason is the coach's own words. */}
                <span className="card__title">{b.reason ?? t('co.off.defaultReason')}</span>
                <span className="card__when">{relativeTo(b.starts_at)}</span>
              </div>
              <div className="card__when" style={{ marginTop: 3 }}>
                {fmtLongDay(new Date(b.starts_at))} {fmtTime(b.starts_at)} — {fmtLongDay(new Date(b.ends_at))} {fmtTime(b.ends_at)}
              </div>
              <div className="card__foot">
                <span />
                <button className="card__cancel" onClick={() => remove(b.id)}>{t('co.off.remove')}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
